// Standard shadcn/ui badge — vendored as source per shadcn convention.
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/cn";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success:
          "border-transparent bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-200",
        warning:
          "border-transparent bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
        muted: "border-transparent bg-muted text-muted-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// CountBadge — the exact-count pill for badge counts (#983, Epic #956 2a):
// sidebar nav rows, the collapsed icon-rail overlay, the user-menu total.
// Built on top of Badge/badgeVariants rather than a parallel component so it
// stays visually consistent with every other badge in the portal; it only
// adds the sizing a single/double/triple-digit count needs and renders the
// LITERAL number always — no "99+"/"100+" truncation, per this issue's AC.
// Renders nothing when count <= 0, so callers can unconditionally mount
// `<CountBadge count={n} />` without their own `n > 0 &&` guard.
export interface CountBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  count: number;
  variant?: BadgeProps["variant"];
}

function CountBadge({ count, className, variant = "destructive", ...props }: CountBadgeProps) {
  if (count <= 0) return null;
  return (
    <Badge
      variant={variant}
      className={cn(
        "h-5 min-w-[1.25rem] items-center justify-center rounded-full border-0 px-1",
        "text-[10px] font-semibold leading-none tabular-nums",
        className,
      )}
      {...props}
    >
      {count}
    </Badge>
  );
}

export { Badge, badgeVariants, CountBadge };
